// PayrollProcessing.js
import { Fragment, useState, useMemo } from "react";
import { Dialog, Transition } from "@headlessui/react";

export default function PayrollProcessing() {
  const [month, setMonth] = useState("01");
  const [year, setYear] = useState("2025");

  const [payroll, setPayroll] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);

  // Dummy assignments (in real app → fetch from EmployeeSalaryAssignment)
  const assignments = [
    {
      employee: "E001 - John Doe",
      structure: "Below 30K - Male",
      components: [
        { name: "Basic", amount: 12000 },
        { name: "HRA", amount: 6000 },
        { name: "Conveyance", amount: 1600 },
      ],
    },
    {
      employee: "E002 - Jane Smith",
      structure: "Below 30K - Female",
      components: [
        { name: "Basic", amount: 11500 },
        { name: "HRA", amount: 5750 },
        { name: "Conveyance", amount: 1600 },
      ],
    },
  ];

  const generatePayroll = () => {
    const processed = assignments.map((a) => {
      const gross = a.components.reduce((sum, c) => sum + c.amount, 0);
      const deductions = Math.round(gross * 0.1); // demo rule: 10% deductions
      const net = gross - deductions;
      return { ...a, gross, deductions, net };
    });
    setPayroll(processed);
  };

  const previewData = useMemo(
    () => (previewIndex != null ? payroll[previewIndex] : null),
    [previewIndex, payroll]
  );

  return (
    <div className="bg-white dark:bg-gray-900 p-4 font-poppins rounded-2xl">
      {/* Header */}
      <h2 className="text-xl font-semibold mb-4">Payroll Processing</h2>

      {/* Controls */}
      <div className="flex gap-3 mb-4">
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          className="border p-2 rounded"
        >
          {[
            "01-Jan",
            "02-Feb",
            "03-Mar",
            "04-Apr",
            "05-May",
            "06-Jun",
            "07-Jul",
            "08-Aug",
            "09-Sep",
            "10-Oct",
            "11-Nov",
            "12-Dec",
          ].map((m, idx) => (
            <option key={idx} value={m.split("-")[0]}>
              {m}
            </option>
          ))}
        </select>
        <input
          type="number"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          className="border p-2 rounded w-28"
        />
        <button
          onClick={generatePayroll}
          className="px-4 py-2 bg-blue-600 text-white rounded"
        >
          Generate Payroll
        </button>
      </div>

      {/* Payroll Table */}
      {payroll.length > 0 && (
        <div className="overflow-x-auto bg-white dark:bg-gray-900 shadow-md rounded-lg font-poppins">
          <table className="min-w-full table-auto md:table-fixed text-sm">
            <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
              <tr>
                <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Employee</th>
                <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Structure</th>
                <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Gross</th>
                <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Deductions</th>
                <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Net Pay</th>
                <th className="py-2 px-4 text-center font-semibold text-xs md:text-sm text-black dark:text-white whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payroll.map((p, i) => (
                <tr key={i} className="border-b dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-950 dark:hover:text-whit">
                  <td className="py-2 px-4 text-xs dark:text-white text-center truncate md:text-sm whitespace-nowrap">{p.employee}</td>
                  <td className="py-2 px-4 text-xs dark:text-white text-center truncate md:text-sm whitespace-nowrap">{p.structure}</td>
                  <td className="py-2 px-4 text-xs dark:text-white text-center truncate md:text-sm whitespace-nowrap">{p.gross.toLocaleString()}</td>
                  <td className="py-2 px-4 text-xs dark:text-white text-center truncate md:text-sm whitespace-nowrap">{p.deductions.toLocaleString()}</td>
                  <td className="py-2 px-4 text-xs dark:text-white text-center truncate md:text-sm whitespace-nowrap">{p.net.toLocaleString()}</td>
                  <td className="px-4 py-2 text-center">
                    <div className="flex gap-2 justify-center items-center">
                    <button
                      onClick={() => {
                        setPreviewIndex(i);
                        setPreviewOpen(true);
                      }}
                      className="px-2 py-1 text-blue-500 hover:underline hover:underline-offset-4"
                    >
                      Preview
                    </button>
                    <button className="px-2 py-1 text-yellow-500 hover:underline hover:underline-offset-4">
                      Lock
                    </button>
                    <button className="px-2 py-1 text-green-500 hover:underline hover:underline-offset-4">
                      Download Slip
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview Modal */}
      <Transition appear show={previewOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setPreviewOpen(false)}>
          <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/25">
            <Dialog.Panel className="w-full max-w-lg rounded bg-white p-6 shadow-lg">
              <Dialog.Title className="text-lg font-semibold">
                Salary Slip Preview
              </Dialog.Title>
              {previewData && (
                <div className="mt-4 text-sm space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div><strong>Employee:</strong> {previewData.employee}</div>
                    <div><strong>Structure:</strong> {previewData.structure}</div>
                    <div><strong>Month:</strong> {month}/{year}</div>
                    <div><strong>Net Pay:</strong> {previewData.net.toLocaleString()}</div>
                  </div>

                  <table className="w-full border text-sm mt-3">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border px-2 py-1 text-center">Component</th>
                        <th className="border px-2 py-1 text-center">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.components.map((c, idx) => (
                        <tr key={idx}>
                          <td className="border px-2 py-1 text-center">{c.name}</td>
                          <td className="border px-2 py-1 text-center">{c.amount.toLocaleString()}</td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-semibold">
                        <td className="border px-2 py-1 text-center">Gross</td>
                        <td className="border px-2 py-1 text-center">{previewData.gross.toLocaleString()}</td>
                      </tr>
                      <tr className="bg-gray-50 font-semibold">
                        <td className="border px-2 py-1 text-center">Deductions</td>
                        <td className="border px-2 py-1 text-center">{previewData.deductions.toLocaleString()}</td>
                      </tr>
                      <tr className="bg-gray-50 font-semibold">
                        <td className="border px-2 py-1 text-center">Net Pay</td>
                        <td className="border px-2 py-1 text-center">{previewData.net.toLocaleString()}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setPreviewOpen(false)}
                  className="px-4 py-2 rounded bg-blue-600 text-white"
                >
                  Close
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
